/**
 * Freeze V2 API — REST-only service layer for freeze-v2.
 *
 * Re-exports from shared @/services/freeze for mutation hooks.
 * Fetch functions are defined here because v1 doesn't export them directly.
 */
import { redirectToLoginIfUnauthorized } from '@/lib/authRedirect';
import { getAccessToken } from '@/lib/tokenStorage';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';

export type {
  AddIntEmbsRequest,
  FertAndEmbryosRequest,
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  LiveEmbryoResponse,
  MassInterimEmbryosRequest,
  TempEmbryoResponse,
  UpdateApprovalNumberRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateODFreezeRequest,
  ValidateFreezeRequest,
} from '@/services/freeze';

export {
  useAddIntEmbsMutation,
  useAddMassInterimEmbryosMutation,
  useApplyApprovalNumbersMutation,
  useDeactivateInterimEmbryosMutation,
  useUpdateEmbryoFieldMutation,
  useUpdateFertilizationFieldMutation,
  useUpdateFreezeEmbryosMutation,
  useUpdateFreezeTempEmbryosMutation,
  useUpdateInterimEmbryoFieldMutation,
  useUpdateODFreezeMutation,
  useUpdateODFreezeV2Mutation,
  useValidateFreezeMutation,
} from '@/services/freeze';

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchFertilizationListFreeze(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  searchQuery?: string | null;
  skip?: number;
  take?: number;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
  if (params.skip != null) searchParams.set('skip', params.skip.toString());
  if (params.take != null) searchParams.set('take', params.take.toString());

  const url = `${getBaseUrl()}/api/v1/freeze/fertilizations?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    redirectToLoginIfUnauthorized(response);
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as FertilizationListFreezeResponse[];
}

export async function fetchFertAndEmbryos(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  fertilizationIds: string[];
}) {
  const url = `${getBaseUrl()}/api/v1/freeze/fertilizations/fert-and-embryos`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    redirectToLoginIfUnauthorized(response);
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as FertAndEmbryosResponse[];
}
